import { Component, OnInit, Output, EventEmitter, ViewChild, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';
import { DataService } from '../../../../../../services/dashboard/data.service';
import { DmtUtilityService } from '../../../../../../services/dashboard/dmt-utility.service';
import { CommonModule } from '@angular/common';
import { TimelineEvolutionRegionalAccess } from '../../../../../../shared/interfaces/nt-EvolutionRegionalAccess.interface';
import { NtChartRegionInterface } from '../../../../../../shared/interfaces/nt-chart-region.interface';
import { forkJoin, max } from 'rxjs';

@Component({
  selector: 'app-evolution-regional-access',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './evolution-regional-access.component.html',
  styleUrl: './evolution-regional-access.component.css'
})
export class EvolutionRegionalAccessComponent implements OnInit, AfterViewInit, OnDestroy {
  @Output() isTwoRowLayout = new EventEmitter<boolean>();

  public medicineId: number = 0;
  public companyId: number = 0;
  public countryId: number = 1;
  public regionId: number = 0;
  public regionName?: string | null;
  stepSelected: string = '';
  hospitalOnekey: string = '';
  hasForecastData: boolean = false;
  hasActualData: boolean = false;
  isTwoRow: boolean = false;
  currentIndex: number = 0;
  maxIndex: number = 0;
  private resizeListener: any;

  public chartData: NtChartRegionInterface = {
    labels: [],
    actual: [],
    real: [],
    target: []
  };

  timelineEvolution: TimelineEvolutionRegionalAccess[] = [];
  isLoadingTimeline: boolean = true;

  constructor(private dataService: DataService, private us: DmtUtilityService) { }
  @ViewChild('carouselEvolutionRegionalAccess') carousel!: ElementRef;

  ngOnInit(): void {
    this.isLoadingTimeline = true;
    this.getSessionVariables();
    this.getRegionalAccessEvolution();
  }

ngAfterViewInit(): void {
  this.resizeListener = this.updateMaxIndex.bind(this);
  window.addEventListener('resize', this.resizeListener);
  }

ngOnDestroy(): void {
  if (this.resizeListener) {
    window.removeEventListener('resize', this.resizeListener);
  }
}

 getRegionalAccessEvolution(): void {
  this.dataService.getRegionalAccessEvolutionData(this.companyId, this.medicineId, this.countryId).subscribe(data => {
   // we can use data=[] to simulate the empty data scenario
   // data=[]

   // Fill missing months in the timeline (or create placeholder if empty)
    if (!data || data.length === 0) {
      this.timelineEvolution = this.createPlaceholderTimeline();
    } else {
      this.timelineEvolution = this.fillMissingMonths(data);
      this.updateMaxIndex();
    }

    // check if there is forecast data in any item
    this.hasForecastData = this.timelineEvolution.some(item => 
      item.forecast && item.forecast.length > 0
    );
    
    // check if there is actual data in any item  
    this.hasActualData = this.timelineEvolution.some(item => 
      item.actual && item.actual.length > 0
    );
    
    // two rows layout if both data series exist
    let hasBothSeries = this.hasForecastData && this.hasActualData;
    
    this.isTwoRowLayout.emit(hasBothSeries);
    this.isTwoRow = hasBothSeries;
    this.isLoadingTimeline = false;
  });
}
  /**
   * Create placeholder timeline with 8 future months starting from current month
   */
  private createPlaceholderTimeline(): TimelineEvolutionRegionalAccess[] {
    const placeholder: TimelineEvolutionRegionalAccess[] = [];
    const today = new Date();
    
    for (let i = 0; i < 8; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() + i, 1);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      
      placeholder.push({
        datePoint: `${year}-${month}`,
        forecast: [],
        actual: []
      });
    }
    
    return placeholder;
  }

  /**
   * Fill missing months between datePoints to show realistic timeline gaps
   */
  private fillMissingMonths(data: TimelineEvolutionRegionalAccess[]): TimelineEvolutionRegionalAccess[] {
    if (!data || data.length === 0) {
      return [];
    }

    // Sort data by date (format is YYYY-MM)
    const sortedData = [...data].sort((a, b) => {
      return a.datePoint.localeCompare(b.datePoint);
    });

    const filledTimeline: TimelineEvolutionRegionalAccess[] = [];
    
    for (let i = 0; i < sortedData.length; i++) {
      // Add current item
      filledTimeline.push(sortedData[i]);

      // If there's a next item, check for gaps
      if (i < sortedData.length - 1) {
        const current = sortedData[i].datePoint; // "YYYY-MM"
        const next = sortedData[i + 1].datePoint; // "YYYY-MM"

        // Generate missing months between current and next
        const missingMonths = this.generateMissingMonths(current, next);
        filledTimeline.push(...missingMonths);
      }
    }

    return filledTimeline;
  }

  /**
   * Generate empty timeline items for missing months
   */
  private generateMissingMonths(start: string, end: string): TimelineEvolutionRegionalAccess[] {
    const missingMonths: TimelineEvolutionRegionalAccess[] = [];
    
    const [startYear, startMonth] = start.split('-').map(Number);
    const [endYear, endMonth] = end.split('-').map(Number);
    
    // Calculate total months difference
    const startTotalMonths = startYear * 12 + startMonth;
    const endTotalMonths = endYear * 12 + endMonth;
    const monthsDiff = endTotalMonths - startTotalMonths;
    
    // If difference is 1 or less, no gap exists
    if (monthsDiff <= 1) {
      return [];
    }
    
    // Generate missing months
    let currentYear = startYear;
    let currentMonth = startMonth + 1;
    
    while (currentYear < endYear || (currentYear === endYear && currentMonth < endMonth)) {
      if (currentMonth > 12) {
        currentMonth = 1;
        currentYear++;
      }
      
      const datePoint = `${currentYear}-${String(currentMonth).padStart(2, '0')}`;
      
      missingMonths.push({
        datePoint: datePoint,
        forecast: [],
        actual: []
      });
      
      currentMonth++;
    }

    return missingMonths;
  }

   private updateMaxIndex(): void {
    const screenWidth = window.innerWidth;
    let maxCards = 6;
    
    if (screenWidth >= 1516) {
      maxCards = 8;
    } else if (screenWidth >= 1280) {
      maxCards = 7;
    } else {
      maxCards = 6;
    }
    
    this.maxIndex = Math.max(0, this.timelineEvolution.length - maxCards);
    
    // if currentIndex is outside of range, step back to maxIndex
    if (this.currentIndex > this.maxIndex) {
      this.currentIndex = this.maxIndex;
      this.scrollToCurrent();
    }
  }


 next() {
    if (this.currentIndex < this.maxIndex) {
      this.currentIndex++;
      this.scrollToCurrent();
    }
  }

  prev() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.scrollToCurrent();
    }
  }
  

private scrollToCurrent() {
  if (this.carousel?.nativeElement) {
    const card = this.carousel.nativeElement.querySelector('.pointInTimeline');
    if (card) {
      // we read the size of each card (in this case the entire datepoint) and we multiply it 
      // with the index number and assign the value to the "left" property
      const cardWidth = card.offsetWidth       
      this.carousel.nativeElement.scrollTo({
        left: this.currentIndex * cardWidth,
        behavior: 'smooth',
      });
    }
  }
}

  getSessionVariables() {
    this.regionName = sessionStorage.getItem('regionName') || '';
    this.medicineId = this.us.getSessionVariableNumber('medicineId', 0);
    this.countryId = this.us.getSessionVariableNumber('countryId');
    this.regionId = this.us.getSessionVariableNumber('regionId');
    this.companyId = this.us.getSessionVariableNumber('companyId', 0);
    this.stepSelected = sessionStorage.getItem('stepSelected') || '';
    this.hospitalOnekey = sessionStorage.getItem('hospitalOnekey') || '';
  }
}